import React, { useState, useEffect } from "react";
import { getContrastTextColorRGB } from "utils/getContrastTextColor";
import { convertToHex, convertToRGB } from "utils/convertColor";
import {
  blockColors,
  circleColors,
  compactColors,
  ghColors,
  sketchColors,
  twitterColors,
} from "styles/theme/colors";
import { CirclePicker } from "react-color";
import Tab from "components/tab";
import { FaLongArrowAltRight } from "react-icons/fa";
import { Button } from "components/buttonV2";
import { IStatus } from "./interface";
import styled from "styled-components";
import { useTranslation } from 'react-i18next';

interface Props {
  status: IStatus;
  currentColor: string;
  colorScheme: string;
  addCustomColor: (id: number, color: string, type: string) => void;
  closeModal: () => void;
  getTagStyle: (type: string, color: string) => any;
}

const ChangeColorModal = ({ status, colorScheme, addCustomColor, closeModal, getTagStyle}: Props) => {
  const { t } = useTranslation();
  const tBase = "views.admin.status-colors.ChangeColorModal";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { 
    id, 
    label, 
    type: statusType, 
    custom_color, 
    color: legacyColor,
    color_v2: originalColor 
    } = status;

  const currentColor = custom_color
    ? custom_color
    : colorScheme === "legacy"
    ? legacyColor
    : originalColor;
    
  const [selectedColors, setSelectedColors] = useState(blockColors);
  const [colorType, setColorType] = useState(""); // custom, legacy, original
  const [colorValue, setColorValue] = useState(convertToHex(currentColor));

  const newColor = (() => {
    if (colorType === "legacy") return legacyColor;
    if (colorType === "original") return originalColor;
    // else "custom"
    return convertToRGB(colorValue);
  })();

  const handleTabClick = (key: string) => {
    switch (key) {
      case "block":
        setSelectedColors(blockColors);
        break;
      case "circle":
        setSelectedColors(circleColors);
        break;
      case "compact":
        setSelectedColors(compactColors);
        break;
      case "gh":
        setSelectedColors(ghColors);
        break;
      case "sketch":
        setSelectedColors(sketchColors);
        break;
      case "twitter":
        setSelectedColors(twitterColors);
        break;
      default:
        setSelectedColors(blockColors);
    }
  };

  const tabData = {
    block: { tab: tr("Block Colors") },
    circle: { tab: tr("Circle Colors") },
    compact: { tab: tr("Compact Colors") },
    gh: { tab: tr("GH Colors") },
    sketch: { tab: tr("Sketch Colors") },
    twitter: { tab: tr("Twitter Colors") },
  };

  const handleChangeColor = (newColor: any) => {
    setColorValue(newColor.hex);
  };

  const handleSave = () => {
    addCustomColor(id, convertToRGB(colorValue), colorType);
    closeModal();
  };

  return (
    <div className="w-[660px] h-[380px] flex flex-col">

      {/* Type */}
      <div className="text-gray-600 text-sm mb-5 flex space-x-1">
        <span>{tr("Type")}:</span>
        <span className="capitalize">{statusType === "service_plan" ? tr("Service Plan") : tr(statusType)}</span>
      </div>

      {/* Color Preview */}
      <div className="flex items-start justify-between">

        {/* Current || New */}
        <div className="flex space-x-5 mb-5 items-end">
          <div>
            <div className="mb-0.5 text-gray-600 text-sm">{tr("Current")}</div>
            <div 
              className="text-white px-3 py-1 rounded-md text-sm font-medium block border" 
              style={getTagStyle(statusType, currentColor)}
            >
              {tr(label)}
            </div>
          </div>
          <FaLongArrowAltRight size={24} />
          <div>
            <div className="mb-0.5 text-gray-600 text-sm">{tr("New")}</div>
            <div 
              className="text-white px-3 py-1 rounded-md text-sm font-medium block border" 
              style={getTagStyle(statusType, newColor)}
            >
              {label}
            </div>
          </div>
        </div>
        
        {/* Original || Legacy */}
        <div className="justify-end items-end flex flex-col space-y-2 mr-4">
          <div className="flex items-center space-x-3">
            <div className="text-gray-600 text-sm">{tr("Legacy")}</div>
            <StyledCirclePickerWrapper isWhite={colorValue === "#ffffff"}>
              <CirclePicker
                color={colorValue}
                colors={[convertToHex(legacyColor)]}
                onChangeComplete={handleChangeColor}
                width="100%"
                onChange={() => setColorType("legacy")}
              />
            </StyledCirclePickerWrapper>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-gray-600 text-sm">{tr("Default")}</div>
            <StyledCirclePickerWrapper isWhite={colorValue === "#ffffff"}>
              <CirclePicker
                color={colorValue}
                colors={[convertToHex(originalColor)]}
                onChangeComplete={handleChangeColor}
                width="100%"
                onChange={() => setColorType("original")}
              />
            </StyledCirclePickerWrapper>
          </div>
        </div>

      </div>

      {/* Color Picker */}
      <div className="flex-1">
        <Tab data={tabData} onClick={handleTabClick} defaultTab="block" />
        <StyledCirclePickerWrapper isWhite={colorValue === "#ffffff"}>
          <CirclePicker
            color={colorValue}
            colors={selectedColors}
            onChangeComplete={handleChangeColor}
            className="mt-4"
            width="100%"
            onChange={() => setColorType("custom")}
          />
        </StyledCirclePickerWrapper>
      </div>

      {/* Buttons */}
      <div className="flex justify-end p-4 space-x-4">
        <Button 
          type="secondary"
          onClick={closeModal}
        >
          {tr("Cancel")}
        </Button>
        <Button 
          onClick={handleSave}
        >
          {tr("Save")}
        </Button>
      </div>
    </div>
  );
};

export default ChangeColorModal;

const StyledCirclePickerWrapper = styled.div<{ isWhite: boolean }>`
  .circle-picker span div {
    ${(props) => "border: 1px solid rgba(0, 0, 0, 0.1) !important;"}
    border-radius: 50% !important;  /* Ensure full circular shape */
  }
`;
